import Box from '@components/Box';
import Link from '@components/Link';
import Typography from '@components/Typography';
import { cssJoin } from '@shared/util';
import type { PropsWithChildren } from 'react';

type Props = PropsWithChildren<{
  title?: string;
  fadeIn?: boolean;
  fullWidth?: boolean;
  orientation?: 'horizontal' | 'vertical';
  severity?: 'error' | 'info' | 'success' | 'warning';
  link?:
    | string
    | {
        href: string;
        label?: string;
      };
}>;

const getBgColor = (variant: Props['severity']): BgColor => {
  switch (variant) {
    case 'success':
      return 'feedback-green';
    case 'warning':
      return 'feedback-orange';
    case 'error':
      return 'feedback-red';
    default:
      return 'feedback-gray';
  }
};

const Alert: IComponent<Props> = ({
  link,
  title,
  children,
  fullWidth,
  fadeIn = false,
  severity = 'info',
  orientation = 'vertical',
}) => (
  <Box
    aria-labelledby={title}
    bgcolor={getBgColor(severity)}
    aria-describedby={typeof children === 'string' ? children : undefined}
    className={cssJoin(
      'p-16',
      'gap-8',
      'flex-wrap',
      'border-l-2',
      fadeIn && 'fade-in',
      fullWidth && 'w-full',
      severity === 'info' && 'border-primary',
      orientation === 'vertical' && 'flex-col',
      orientation === 'horizontal' && 'flex-row',
      severity === 'error' && 'border-feedback-red',
      severity === 'success' && 'border-feedback-green',
      severity === 'warning' && 'border-feedback-orange',
    )}
  >
    {title && <h3 className={cssJoin('font-medium')}>{title}</h3>}
    {typeof children === 'string' ? (
      <>
        <Typography variant="Body 14 - Standard">{children}</Typography>
        {typeof link === 'string' ? (
          <Link variant="underlined" href={link}>
            Learn more
          </Link>
        ) : link ? (
          <Link variant="underlined" href={link.href}>
            {link.label ?? 'Learn more'}
          </Link>
        ) : null}
      </>
    ) : (
      children
    )}
  </Box>
);

export default Alert;
